/**
 * LeftShield Portal - Policy Exceptions Page
 * Manage governed exception requests, approvals, and budget tracking.
 *
 * Route: /policy-exceptions | Nav: Compliance > Policy Exceptions
 */
import React, { useState } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  Button,
  Spinner,
  tokens,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Input,
  Textarea,
} from '@fluentui/react-components';
import {
  CheckmarkCircleRegular,
  DismissCircleRegular,
  ShieldCheckmarkRegular,
  ClockRegular,
  AddRegular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { exceptionsApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import type { PolicyException, ExceptionAnalytics, ExceptionRequestPayload } from '../types';

const statusBadgeColor = (
  status: string,
): 'warning' | 'success' | 'informative' | 'danger' | 'subtle' => {
  switch (status) {
    case 'Pending':
      return 'warning';
    case 'Active':
      return 'success';
    case 'Expired':
      return 'informative';
    case 'Denied':
      return 'danger';
    case 'Revoked':
      return 'subtle';
    default:
      return 'subtle';
  }
};

const pressureBandColor = (
  band: string,
): 'success' | 'warning' | 'danger' | 'informative' => {
  switch (band.toLowerCase()) {
    case 'low':
      return 'success';
    case 'medium':
      return 'warning';
    case 'high':
    case 'critical':
      return 'danger';
    default:
      return 'informative';
  }
};

export const PolicyExceptionsPage: React.FC = () => {
  const ps = usePageStyles();
  const queryClient = useQueryClient();

  // Request Exception dialog state
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const [reqPolicyRuleId, setReqPolicyRuleId] = useState('');
  const [reqReason, setReqReason] = useState('');
  const [reqScope, setReqScope] = useState('');
  const [reqTtlHours, setReqTtlHours] = useState('72');

  const requestExceptionMutation = useMutation({
    mutationFn: (payload: ExceptionRequestPayload) => exceptionsApi.requestException(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exceptions'] });
      setIsRequestDialogOpen(false);
      setReqPolicyRuleId('');
      setReqReason('');
      setReqScope('');
      setReqTtlHours('72');
    },
  });

  const handleSubmitExceptionRequest = () => {
    const payload: ExceptionRequestPayload = {
      policyRuleId: Number(reqPolicyRuleId),
      reason: reqReason,
      scopeJson: reqScope || undefined,
      ttlHours: reqTtlHours ? Number(reqTtlHours) : undefined,
    };
    requestExceptionMutation.mutate(payload);
  };

  const { data: exceptions, isLoading: exceptionsLoading } = useQuery<PolicyException[]>({
    queryKey: ['exceptions', 'list'],
    queryFn: () => exceptionsApi.list(),
  });

  const { data: analytics, isLoading: analyticsLoading } = useQuery<ExceptionAnalytics>({
    queryKey: ['exceptions', 'analytics'],
    queryFn: () => exceptionsApi.getAnalytics(),
  });

  const { data: pending, isLoading: pendingLoading } = useQuery<PolicyException[]>({
    queryKey: ['exceptions', 'pending'],
    queryFn: () => exceptionsApi.getPending(),
  });

  const approveMutation = useMutation({
    mutationFn: (id: number) => exceptionsApi.approve(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['exceptions'] }),
  });

  const denyMutation = useMutation({
    mutationFn: (id: number) => exceptionsApi.deny(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['exceptions'] }),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: number) => exceptionsApi.revoke(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['exceptions'] }),
  });

  const isLoading = exceptionsLoading || analyticsLoading || pendingLoading;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
        <Spinner size="large" label="Loading policy exceptions..." />
      </div>
    );
  }

  const totalActive = analytics?.totalActive ?? 0;
  const totalPending = analytics?.totalPending ?? 0;
  const pressurePct = analytics?.exceptionPressurePct ?? 0;
  const pressureBand = analytics?.exceptionPressureBand ?? 'Unknown';
  const budgetUsage = analytics?.budgetUsage ?? [];
  const budgetExceeded = budgetUsage.some((b) => b.isExceeded);

  return (
    <div className={ps.page}>
      <PageHeader
        title="Risk Acceptance"
        subtitle="Governed risk acceptance decisions with budget tracking and automatic expiry"
        actions={
          <Dialog open={isRequestDialogOpen} onOpenChange={(_, data) => setIsRequestDialogOpen(data.open)}>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="primary" icon={<AddRegular />}>Accept Risk</Button>
            </DialogTrigger>
            <DialogSurface>
              <DialogTitle>Request Policy Exception</DialogTitle>
              <DialogBody>
                <DialogContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                    <Field label="Policy ID" required>
                      <Input
                        type="number"
                        value={reqPolicyRuleId}
                        onChange={(_, data) => setReqPolicyRuleId(data.value)}
                        placeholder="e.g., 101"
                      />
                    </Field>
                    <Field label="Reason" required>
                      <Textarea
                        value={reqReason}
                        onChange={(_, data) => setReqReason(data.value)}
                        placeholder="Describe why this exception is needed..."
                        rows={3}
                      />
                    </Field>
                    <Field label="Scope" hint="Optional — JSON scope expression">
                      <Input
                        value={reqScope}
                        onChange={(_, data) => setReqScope(data.value)}
                        placeholder='e.g., {"repo": "my-repo"}'
                      />
                    </Field>
                    <Field label="TTL (hours)" hint="How long the exception should last">
                      <Input
                        type="number"
                        value={reqTtlHours}
                        onChange={(_, data) => setReqTtlHours(data.value)}
                        placeholder="72"
                      />
                    </Field>
                  </div>
                </DialogContent>
                <DialogActions>
                  <Button appearance="secondary" onClick={() => setIsRequestDialogOpen(false)}>Cancel</Button>
                  <Button
                    appearance="primary"
                    onClick={handleSubmitExceptionRequest}
                    disabled={!reqPolicyRuleId || !reqReason || requestExceptionMutation.isPending}
                  >
                    {requestExceptionMutation.isPending ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        }
      />

      {/* Analytics Summary Cards */}
      <div className={ps.metricGrid}>
        <Card>
          <CardHeader
            image={
              <CheckmarkCircleRegular
                style={{ fontSize: '1.5rem', color: totalActive > 0 ? tokens.colorPaletteGreenForeground1 : tokens.colorNeutralForeground3 }}
              />
            }
            header={<Body2>Total Active</Body2>}
            description={<Title2 className="metric-value">{totalActive}</Title2>}
          />
        </Card>
        <Card>
          <CardHeader
            image={
              <ClockRegular
                style={{ fontSize: '1.5rem', color: totalPending > 0 ? tokens.colorPaletteYellowForeground1 : tokens.colorNeutralForeground3 }}
              />
            }
            header={<Body2>Total Pending</Body2>}
            description={<Title2 className="metric-value">{totalPending}</Title2>}
          />
        </Card>
        <Card>
          <CardHeader
            image={
              <ShieldCheckmarkRegular
                style={{ fontSize: '1.5rem', color: tokens.colorBrandForeground1 }}
              />
            }
            header={<Body2>Exception Pressure</Body2>}
            description={
              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                <Title2 className="metric-value">{pressurePct}%</Title2>
                <Badge appearance="filled" color={pressureBandColor(pressureBand)}>
                  {pressureBand}
                </Badge>
              </div>
            }
          />
        </Card>
        <Card>
          <CardHeader
            image={
              <DismissCircleRegular
                style={{ fontSize: '1.5rem', color: budgetExceeded ? tokens.colorPaletteRedForeground1 : tokens.colorPaletteGreenForeground1 }}
              />
            }
            header={<Body2>Budget Status</Body2>}
            description={
              <Title2
                className="metric-value"
                style={{ color: budgetExceeded ? tokens.colorPaletteRedForeground1 : tokens.colorPaletteGreenForeground1 }}
              >
                {budgetExceeded ? 'Exceeded' : 'Within Limits'}
              </Title2>
            }
          />
        </Card>
      </div>

      {/* Pending Exceptions */}
      {pending && pending.length > 0 && (
        <Card>
          <CardHeader
            header={<Title2>Pending Approvals</Title2>}
            description="Exception requests awaiting review"
          />
          <div className={ps.cardBody}>
            <div className={ps.rowList}>
              {pending.map((ex) => (
                <div key={ex.id} className={ps.rowItem}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flex: 1 }}>
                    <Badge appearance="filled" color="warning" size="small">
                      Pending
                    </Badge>
                    <Body1 style={{ fontWeight: 500 }}>{ex.policyClass}</Body1>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                      Scope: {ex.scope}
                    </Body2>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                      by {ex.requestedBy}
                    </Body2>
                    <Button
                      appearance="primary"
                      size="small"
                      onClick={() => approveMutation.mutate(ex.id)}
                      disabled={approveMutation.isPending}
                    >
                      Approve
                    </Button>
                    <Button
                      appearance="subtle"
                      size="small"
                      onClick={() => denyMutation.mutate(ex.id)}
                      disabled={denyMutation.isPending}
                    >
                      Deny
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* All Exceptions List */}
      <Card>
        <CardHeader
          header={<Title2>All Exceptions</Title2>}
          description="Complete list of exception requests and their current status"
        />
        <div className={ps.cardBody}>
          {exceptions && exceptions.length > 0 ? (
            <div className={ps.rowList}>
              {exceptions.map((ex) => (
                <div key={ex.id} className={ps.rowItem}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flex: 1 }}>
                    <Badge appearance="filled" color={statusBadgeColor(ex.status)} size="small">
                      {ex.status}
                    </Badge>
                    <Body1 style={{ fontWeight: 500 }}>{ex.policyClass}</Body1>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                      {ex.reason}
                    </Body2>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                      Scope: {ex.scope}
                    </Body2>
                    {ex.expiresAt && (
                      <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                        Expires: {new Date(ex.expiresAt).toLocaleDateString()}
                      </Body2>
                    )}
                    {ex.status === 'Active' && (
                      <Button
                        appearance="subtle"
                        size="small"
                        onClick={() => revokeMutation.mutate(ex.id)}
                        disabled={revokeMutation.isPending}
                      >
                        Revoke
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalXXL}`,
                gap: tokens.spacingVerticalM,
                textAlign: 'center',
              }}
            >
              <ShieldCheckmarkRegular style={{ fontSize: '3rem', color: tokens.colorNeutralForeground3 }} />
              <Body1 style={{ fontWeight: 600 }}>No Active Risk Exceptions</Body1>
              <Body2 style={{ color: tokens.colorNeutralForeground3, maxWidth: '420px' }}>
                System Status: All policies fully enforced. No accepted production risk. Recommendation: Maintain strict enforcement posture.
              </Body2>
            </div>
          )}
        </div>
      </Card>

      {/* Budget Usage Breakdown */}
      {budgetUsage.length > 0 && (
        <Card>
          <CardHeader
            header={<Title2>Budget Usage by Scope</Title2>}
            description="Exception budget consumption across scopes"
          />
          <div className={ps.cardBody}>
            <div className={ps.statGrid}>
              {budgetUsage.map((b) => (
                <div key={`${b.scopeType}-${b.scopeId}`} className={ps.statTile}>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{b.scopeType}</Body2>
                  <Title2
                    style={{
                      color: b.isExceeded ? tokens.colorPaletteRedForeground1 : tokens.colorPaletteGreenForeground1,
                    }}
                  >
                    {b.used} / {b.limit}
                  </Title2>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{b.scopeId}</Body2>
                  {b.isExceeded && (
                    <Badge appearance="filled" color="danger" size="small">
                      Exceeded
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
